import { createElement } from 'react';
import { createRoot } from 'react-dom/client';
import { CitizenContextProvider } from '@accenturecodefoundry/mfe-pot-citizen-context';
import { EmploymentSummaryProvider } from '@accenturecodefoundry/mfe-pot-epayroll';
import { App } from './app/App';
import { assetBaseUrl } from './app/asset-base-url';
import { loadRuntimeConfig } from './runtime-config';

/**
 * Only runs in standalone mode (`nx serve` or the plain container's own
 * `index.html`) -- when this app is federated into a shell, the shell
 * imports the exposed `./Component` directly and this file never
 * executes. That's exactly why the <CitizenContextProvider> mount lives
 * here rather than in App.tsx: the shell mounts its own Provider above
 * AppFrame, and both are wired as a federation-shared singleton in
 * federation.config.mjs, so a second Provider inside a federated App.tsx
 * would clobber the shell's cache. Standalone runs have no shell, so
 * `useCitizenContext()` would throw unless *this* file wraps the tree.
 * <EmploymentSummaryProvider> (epayroll, spec 012) is mounted here for the
 * same reason, nested one level in like msca-shell's own App.tsx does:
 * `useEmploymentSummary()` throws outside a Provider too.
 *
 * Awaits loadRuntimeConfig once so both Providers' `baseUrl` props are
 * set before first render -- the Provider fires its own initial
 * fetch on mount, so passing a still-`undefined` value here would race.
 */
async function bootstrap(): Promise<void> {
  const container = document.getElementById('root');
  if (!container) {
    return;
  }
  const config = await loadRuntimeConfig(assetBaseUrl);
  createRoot(container).render(
    createElement(
      CitizenContextProvider,
      { baseUrl: config.citizenContextBaseUrl },
      createElement(EmploymentSummaryProvider, { baseUrl: config.epayrollUbeBaseUrl }, createElement(App)),
    ),
  );
}

void bootstrap();
